<template>
  <NiceView
    :aboveTitle="generalStore.viewHeader"
    :title="CommonService.routes.CHECKIN.title"
    :icon="CommonService.routes.CHECKIN.icon"
    :loading="loading"
    flexBody
  >
    <!-- Filters -->
    <NiceFilters v-model="filtersList" @change="filterChanged" :showCreateButton="false">
      <!-- Refresh -->
      <NiceButton icon="icon-refresh-cw" :title="$gettext('Refresh')" @click="getData" plain :loading="loading"></NiceButton>
    </NiceFilters>

    <!-- Table -->
    <NiceTable
      :actions="actions"
      :columns="columns"
      :data="data"
      :loading="loading"
      freezeLastColumn
      showFooter
      showLimit
      paginated
      @filterChange="filterChanged"
    />

    <!-- <div class="mt-2">
      <div class="d-flex align-center gap-1 mb-1 lh-1">
        <div class="nice-indicator active"></div>
        <small class="text-muted">{{ $gettext("Guest not checked in") }}</small>
      </div>
      <div class="d-flex align-center gap-1 mb-1 lh-1">
        <div class="nice-indicator"></div>
        <small class="text-muted">{{ $gettext("Guest already checked in") }}</small>
      </div>
      <div class="d-flex align-center gap-1 mb-1 lh-1" v-if="modules.PLUS_ONES">
        <div class="nice-indicator partial"></div>
        <small class="text-muted">{{ $gettext("Guest partially checked in") }}</small>
      </div>
    </div> -->

    <!-- Confirm checkout -->
    <NiceConfirmModal
      name="checkin"
      @confirm="confirmCheckin"
      :title="$gettext('Checkin')"
      :message="$gettext('Are you sure you want to checkin \'%{name}\'?', { name: selectedName })"
    />

    <!-- Checkin -->
    <CheckinGuest :guest="selectedItem" @success="getData"></CheckinGuest>

    <!-- <pre>{{ data }}</pre> -->
  </NiceView>
</template>

<script setup>
import { ref, inject, computed } from "vue";
import { formatDateWithTime } from "@/plugins/date";
import { isAfter } from "date-fns";
const $getQuery = inject("getQuery");
const $nice = inject("nice");

import CommonService from "@/services/common.service";
import ErrorService from "@/services/error.service";
import ApiService from "@/services/api/api.service";
import CheckinGuest from "@/components/modals/CheckinGuest.vue";

import { useGettext } from "vue3-gettext";
const { $gettext } = useGettext();

import { useRoute, useRouter } from "vue-router";
const route = useRoute();
const router = useRouter();

import { useGeneralStore } from "@/store/general.store";
const generalStore = useGeneralStore();
const permissions = ref(generalStore.permissions);
const modules = ref(generalStore.modules);

const data = ref([]);
const loading = ref(false);
const selectedItem = ref(null);
const organizationId = router.currentRoute.value.params.organizationId;
const eventId = router.currentRoute.value.params.eventId;
const selectedName = computed(() => (selectedItem.value ? selectedItem.value.first_name + " " + selectedItem.value.last_name : "-"));

async function confirmCheckin() {
  loading.value = true;
  try {
    await ApiService.guests.checkin(organizationId, eventId, selectedItem.value.id);
    $nice.modal("checkin", false);
    CommonService.success($gettext("Guest '%{name}' was checked in", { name: selectedName.value }));
    selectedItem.value = null;
    getData();
  } catch (e) {
    ErrorService.showError(e);
  } finally {
    loading.value = false;
  }
}

function checkinGuest(item) {
  selectedItem.value = item;
  // $nice.modal("checkin", true);
  $nice.modal("checkin-guest", true);
}

async function getData() {
  loading.value = true;
  try {
    const query = await $getQuery();
    query.status = "ACTIVE";
    data.value = await ApiService.guests.getAll(route.params.organizationId, route.params.eventId, query);
    data.value.forEach((v) => {
      v._checkin_expired = isAfter(new Date(), v.group.allow_entry_until);
      v._checkin_status = { title: $gettext("Guest not checked in"), class: "active" };
      if (v.is_checked_in) v._checkin_status = { title: $gettext("Guest already checked in"), class: "" };
      if (v.group.allow_plus_one && v.checked_in_plus_one_count < v.plus_one_count) {
        v._checkin_status = { title: $gettext("Guest partially checked in"), class: "partial" };
      }
    });
  } catch (e) {
    ErrorService.showError(e);
  } finally {
    loading.value = false;
  }
}

const filtersList = [
  {
    name: $gettext("Checked in"),
    type: "yesno",
    key: "is_checked_in",
  },
  {
    name: $gettext("Group"),
    type: "select",
    key: "group",
    valueName: "name",
    searchFunction: searchGroups,
  },
  {
    name: $gettext("Partner"),
    type: "select",
    key: "partner",
    valueName: "name",
    searchFunction: searchPartners,
  },
];

function filterChanged() {
  getData();
}

async function searchGroups(keyword) {
  return ApiService.groups.getAll(organizationId, eventId, { search: keyword });
}

async function searchPartners(keyword) {
  return ApiService.partners.getAll(organizationId, eventId, { search: keyword });
}

const actions = [
  {
    icon: () => "icon-arrow-right",
    title: () => $gettext("Checkin"),
    function: (v) => checkinGuest(v),
    hidden: () => !permissions.value.can_checkin_guests,
    disabled: (v) => v._checkin_expired,
  },
];

const columns = [
  {
    // name: $gettext("Status"),
    key: "is_checked_in",
    class: "w-0 pr-0",
    html: (v, row) => `<div title="${row._checkin_status.title}" class="nice-indicator ${row._checkin_status.class}"></div>`,
    hasOrder: true,
  },
  {
    name: $gettext("Name"),
    key: "first_name",
    html: (_, row) => `
      <div class="d-flex f-center gap-3">
        <div>
          <div>${row.first_name} ${row.last_name}</div>
          ${row.company_name ? `<small class="d-block text-muted">${row.company_name}</small>` : ""}
        </div>
        ${
          modules.value.PLUS_ONES && row.group.allow_plus_one && row.plus_one_count > 0
            ? `<div class="badge badge-primary">+${row.plus_one_count}</div>`
            : ""
        }
      </div>
    `,
    hasOrder: true,
  },
  // {
  //   name: $gettext("First name"),
  //   key: "first_name",
  // },
  // {
  //   name: $gettext("Last name"),
  //   key: "last_name",
  // },
  // {
  //   name: $gettext("Email"),
  //   key: "email",
  // },
  // {
  //   name: $gettext("Plus one"),
  //   key: "checked_in_plus_one_count",
  //   class: "text-right",
  //   disabled: !modules.value.PLUS_ONES,
  //   html: (v) => `<div class="badge badge-primary">+${v}</div>`,
  // },
  {
    name: $gettext("Group"),
    key: "group",
    class: "text-center",
    html: (v) => `
      <div class="badge" style="background-color: ${v.color};">${v.name}</div>
    `,
    hasOrder: true,
  },
  // {
  //   name: $gettext("Partner"),
  //   key: "partner",
  //   html: (v) => `${v ? v.name : "-"}`,
  // },
  {
    name: $gettext("Checked in"),
    key: "checked_in_by",
    class: "w-0 no-wrap",
    html: (v, row) => `
      <div>${formatDateWithTime(row.checkin_timestamp, true)}</div>
      <small>${v ? v.first_name + " " + v.last_name : ""}</small>
      ${row._checkin_expired && !row.checkin_timestamp ? `<div class="red">${$gettext("Checkin time expired")}</div>` : ""}
      ${!row._checkin_expired && !row.checkin_timestamp ? `<div class="red">${$gettext("No")}</div>` : ""}
    `,
    hasOrder: true,
  },
];
</script>
